// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import * as React from "react";
import { useTranslations } from "next-intl";
import { Bell } from "lucide-react";
import { Button, Dialog, EmptyState, Skeleton, Spinner } from "@chotca/ui";
import { ErrorState } from "@/components/error-state";
import {
  useInfiniteNotifications,
  useMarkAllNotificationsRead,
} from "@chotca/api-client";
import { NotificationItem } from "./notifications-popover";

export function NotificationsAllDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const t = useTranslations("notifications");
  const query = useInfiniteNotifications(open);
  const markAll = useMarkAllNotificationsRead();
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);

  const items = React.useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.data.items),
    [query.data],
  );
  const unreadCount = query.data?.pages[0]?.data.unreadCount ?? 0;

  React.useEffect(() => {
    if (!open) return;
    const node = sentinelRef.current;
    if (!node) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (
            entry.isIntersecting &&
            query.hasNextPage &&
            !query.isFetchingNextPage
          ) {
            void query.fetchNextPage();
          }
        }
      },
      { root: node.parentElement, rootMargin: "120px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [open, query.hasNextPage, query.isFetchingNextPage, query.fetchNextPage]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("allTitle")}
      size="lg"
      footer={
        <Button
          variant="secondary"
          size="sm"
          disabled={unreadCount === 0 || markAll.isPending}
          onClick={() => markAll.mutate()}
        >
          {t("markAllRead")}
        </Button>
      }
    >
      <div className="-mx-6 max-h-[65vh] overflow-y-auto border-y border-smoke">
        {query.isLoading ? (
          <div className="space-y-3 p-4">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : query.isError ? (
          <div className="p-4">
            <ErrorState onRetry={() => query.refetch()} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={<Bell className="h-7 w-7" />}
              title={t("empty")}
            />
          </div>
        ) : (
          <ul className="divide-y divide-smoke">
            {items.map((n) => (
              <NotificationItem key={n.id} n={n} onNavigate={onClose} />
            ))}
          </ul>
        )}
        <div ref={sentinelRef} className="flex justify-center py-3">
          {query.isFetchingNextPage ? (
            <span className="flex items-center gap-2 text-body-sm text-ash">
              <Spinner size="sm" />
              {t("loadingMore")}
            </span>
          ) : !query.hasNextPage && items.length > 0 ? (
            <span className="text-body-sm text-ash">{t("endOfList")}</span>
          ) : null}
        </div>
      </div>
    </Dialog>
  );
}
